import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Users, Link2, Check, Loader2, Mail, UserPlus, AlertCircle } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const COLORS = {
  royalBlue: '#002366',
  oxblood: '#800000',
  parchment: '#F5F5DC',
  gold: '#D4AF37'
};

const LChaimLinkAccount = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [linkedAccount, setLinkedAccount] = useState(null);
  const [linkMode, setLinkMode] = useState('existing'); // 'existing' or 'create'
  
  const [formData, setFormData] = useState({
    studentEmail: '',
    studentName: '',
    studentPassword: ''
  });

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const token = localStorage.getItem('academy_token');
      if (!token) {
        navigate('/academy/lchaim/login');
        return;
      }

      const userRes = await api.get('/academy/auth/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (userRes.data.success) {
        setUser(userRes.data.user);
      }

      // Check for existing linked account
      const linkedRes = await api.get('/academy/lchaim/linked-account', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (linkedRes.data.success && linkedRes.data.linked_account) {
        setLinkedAccount(linkedRes.data.linked_account);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLinkExisting = async () => {
    if (!formData.studentEmail) {
      toast.error('Please enter your son\'s email address');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('academy_token');
      const res = await api.post('/academy/lchaim/link-account', {
        student_email: formData.studentEmail,
        relationship: 'parent'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.success) {
        toast.success('Account linked successfully!');
        navigate('/academy/lchaim/companion');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to link account';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAndLink = async () => {
    if (!formData.studentName || !formData.studentEmail || !formData.studentPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (formData.studentPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('academy_token');
      
      // Create student account
      const createRes = await api.post('/academy/lchaim/create-student', {
        name: formData.studentName,
        email: formData.studentEmail,
        password: formData.studentPassword,
        parent_id: user?.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (createRes.data.success) {
        toast.success('Student account created and linked!');
        navigate('/academy/lchaim/companion');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create account';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Already linked
  if (linkedAccount) {
    return (
      <div className="min-h-screen py-12 px-6" style={{ backgroundColor: COLORS.parchment }}>
        <div className="max-w-lg mx-auto">
          <Card style={{ backgroundColor: 'white', border: `2px solid ${COLORS.royalBlue}` }}>
            <CardContent className="p-8 text-center">
              <div 
                className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6"
                style={{ backgroundColor: COLORS.royalBlue }}
              >
                <Check className="w-10 h-10" style={{ color: COLORS.gold }} />
              </div>
              
              <h2 className="text-2xl font-serif font-bold mb-2" style={{ color: COLORS.royalBlue }}>
                Account Linked
              </h2>
              <p className="font-serif mb-6" style={{ color: COLORS.oxblood }}>
                You're connected with {linkedAccount.student_email}
              </p>

              <Button 
                onClick={() => navigate('/academy/lchaim/companion')}
                className="w-full py-6 font-serif font-bold"
                style={{ backgroundColor: COLORS.oxblood, color: COLORS.parchment }}
              >
                Go to Coach's Companion
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-6" style={{ backgroundColor: COLORS.parchment }}>
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img 
            src="/lchaim-logo.png" 
            alt="L'Chaim" 
            className="h-12 mx-auto mb-4"
          />
          <h1 className="text-2xl md:text-3xl font-serif font-bold mb-2" style={{ color: COLORS.royalBlue }}>
            Link Your Son's Account
          </h1>
          <p className="font-serif" style={{ color: COLORS.oxblood }}>
            Connect to access the Coach's Companion
          </p>
        </div>

        {/* Mode Toggle */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={linkMode === 'existing' ? 'default' : 'outline'}
            onClick={() => setLinkMode('existing')}
            className="flex-1 font-serif"
            style={linkMode === 'existing' 
              ? { backgroundColor: COLORS.royalBlue, color: COLORS.parchment }
              : { borderColor: COLORS.royalBlue, color: COLORS.royalBlue }
            }
          >
            <Link2 className="w-4 h-4 mr-2" /> Link Existing
          </Button>
          <Button
            variant={linkMode === 'create' ? 'default' : 'outline'}
            onClick={() => setLinkMode('create')}
            className="flex-1 font-serif"
            style={linkMode === 'create' 
              ? { backgroundColor: COLORS.royalBlue, color: COLORS.parchment }
              : { borderColor: COLORS.royalBlue, color: COLORS.royalBlue }
            }
          >
            <UserPlus className="w-4 h-4 mr-2" /> Create New
          </Button>
        </div>

        {/* Link Existing Account */}
        {linkMode === 'existing' && (
          <Card style={{ backgroundColor: 'white', border: `2px solid ${COLORS.royalBlue}` }}>
            <CardHeader>
              <CardTitle className="font-serif flex items-center gap-2" style={{ color: COLORS.royalBlue }}>
                <Link2 className="w-5 h-5" />
                Link Existing Account
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 rounded flex items-start gap-3" style={{ backgroundColor: COLORS.parchment }}>
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: COLORS.oxblood }} />
                <p className="text-sm font-serif" style={{ color: COLORS.royalBlue }}>
                  Your son must already have an L'Chaim account. Enter his email to send a link request.
                </p>
              </div>

              <div>
                <Label className="font-serif" style={{ color: COLORS.royalBlue }}>Student's Email</Label>
                <Input
                  name="studentEmail"
                  type="email"
                  value={formData.studentEmail}
                  onChange={handleInputChange}
                  placeholder="Enter your son's email"
                  className="mt-1 font-serif"
                  style={{ borderColor: COLORS.royalBlue }}
                />
              </div>

              <Button 
                onClick={handleLinkExisting}
                className="w-full py-6 font-serif font-bold"
                style={{ backgroundColor: COLORS.oxblood, color: COLORS.parchment }}
                disabled={loading}
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Linking...</>
                ) : (
                  <><Link2 className="w-4 h-4 mr-2" /> Link Account</>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Create New Account */}
        {linkMode === 'create' && (
          <Card style={{ backgroundColor: 'white', border: `2px solid ${COLORS.royalBlue}` }}>
            <CardHeader>
              <CardTitle className="font-serif flex items-center gap-2" style={{ color: COLORS.royalBlue }}>
                <UserPlus className="w-5 h-5" />
                Create Student Account
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 rounded flex items-start gap-3" style={{ backgroundColor: COLORS.parchment }}>
                <Users className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: COLORS.oxblood }} />
                <p className="text-sm font-serif" style={{ color: COLORS.royalBlue }}>
                  Create an account for your son. He'll use these credentials to log in and access his dashboard.
                </p>
              </div>

              <div>
                <Label className="font-serif" style={{ color: COLORS.royalBlue }}>Son's Full Name</Label>
                <Input
                  name="studentName"
                  value={formData.studentName}
                  onChange={handleInputChange}
                  placeholder="Enter your son's name"
                  className="mt-1 font-serif"
                  style={{ borderColor: COLORS.royalBlue }}
                />
              </div>

              <div>
                <Label className="font-serif" style={{ color: COLORS.royalBlue }}>Son's Email</Label>
                <Input
                  name="studentEmail"
                  type="email"
                  value={formData.studentEmail}
                  onChange={handleInputChange}
                  placeholder="Enter an email for your son"
                  className="mt-1 font-serif"
                  style={{ borderColor: COLORS.royalBlue }}
                />
              </div>

              <div>
                <Label className="font-serif" style={{ color: COLORS.royalBlue }}>Password</Label>
                <Input
                  name="studentPassword"
                  type="password"
                  value={formData.studentPassword}
                  onChange={handleInputChange}
                  placeholder="Create a password (min 6 characters)"
                  className="mt-1 font-serif"
                  style={{ borderColor: COLORS.royalBlue }}
                />
              </div>

              <Button 
                onClick={handleCreateAndLink}
                className="w-full py-6 font-serif font-bold"
                style={{ backgroundColor: COLORS.oxblood, color: COLORS.parchment }}
                disabled={loading}
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</>
                ) : (
                  <><UserPlus className="w-4 h-4 mr-2" /> Create & Link Account</>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Back Button */}
        <Button 
          variant="ghost"
          onClick={() => navigate('/academy/lchaim/dashboard')}
          className="w-full mt-4 font-serif"
          style={{ color: COLORS.royalBlue }}
        >
          Back to Dashboard
        </Button>
      </div>
    </div>
  );
};

export default LChaimLinkAccount;
